import { NavLink, useLocation } from 'react-router-dom';
import { useBottomNav } from '../contexts/bottom-nav-context';
import { useTranslation } from 'react-i18next';
import { Dumbbell, User } from 'lucide-react';
import type { ComponentType } from 'react';
import { cn } from '../lib/utils';
import { BOTTOM_PILL_CLASS, BOTTOM_PILL_SWAP_MS } from './bottom-pill';
import { useAuth } from '../hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import ClimberIcon from './icons/climber-icon';

type IconRenderProps = { active: boolean };
type IconComponent = ComponentType<IconRenderProps>;

type NavItem = {
  to: string;
  Icon: IconComponent;
  label: string;
  isActive?: (pathname: string) => boolean;
};

export default function BottomNav() {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  // A page with controls of its own borrows this pill rather than covering it
  // with a second one — see BottomNavProvider.
  const { setSlot, filled, hidden } = useBottomNav();

  // Nothing to show and nobody borrowing, or a page that wants the screen whole:
  // no pill at all.
  if (hidden || (!isAuthenticated && !filled)) return null;

  const avatarSrc = user?.avatar_small_url || user?.avatar_url;
  const avatarAlt = user?.full_name || user?.email || 'User';

  const items: NavItem[] = [
    {
      to: '/dashboard',
      label: t('bottomNav.dashboard'),
      Icon: ({ active }) => (
        <Avatar
          className={cn(
            'h-7 w-7 transition-transform border',
            active ? 'border-primary scale-110' : 'border-transparent',
          )}
        >
          <AvatarImage src={avatarSrc} alt={avatarAlt} />
          <AvatarFallback>
            <User className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      ),
    },
    {
      to: '/climbs',
      Icon: ({ active }) => (
        <ClimberIcon
          className={cn('h-5 w-5 transition-transform', active && 'scale-110')}
          strokeWidth={active ? 2.4 : 1.8}
        />
      ),
      label: t('bottomNav.climbs'),
    },
    {
      to: '/training-diary',
      Icon: ({ active }) => (
        <Dumbbell
          className={cn('h-5 w-5 transition-transform', active && 'scale-110')}
          strokeWidth={active ? 2.4 : 1.8}
        />
      ),
      label: t('bottomNav.trainingDiary'),
    },
  ];

  return (
    <nav
      aria-label={t('bottomNav.label')}
      className={cn(
        BOTTOM_PILL_CLASS,
        // The navigation is for phones; a page's controls are for whoever is
        // drawing, at any width — so the pill stays out on a desktop only while
        // it is lent out.
        !filled && 'md:hidden',
      )}
    >
      {/* Both sets of controls stack in one cell, so the pill is as wide as the
          wider of them and the swap has no width to jump. Whichever is not in
          use fades out and stops answering, rather than being taken away: that
          is what makes the change read as this pill turning over instead of one
          bar leaving and another arriving. */}
      <div className="grid grid-cols-1 grid-rows-1 items-center">
        <div
          className={cn(
            'col-start-1 row-start-1 flex items-center gap-0.5 transition-all',
            filled && 'pointer-events-none invisible scale-95 opacity-0',
          )}
          style={{ transitionDuration: `${BOTTOM_PILL_SWAP_MS}ms` }}
        >
          {isAuthenticated &&
            items.map((item) => {
              const manuallyActive = item.isActive?.(location.pathname);

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={!item.isActive}
                  aria-label={item.label}
                  onClick={() => window.scrollTo({ top: 0, left: 0 })}
                  className={({ isActive }) => {
                    const active = manuallyActive ?? isActive;
                    return cn(
                      'flex items-center justify-center',
                      'h-10 w-14 rounded-full transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active
                        ? 'bg-primary/15 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                    );
                  }}
                >
                  {({ isActive }) => {
                    const active = manuallyActive ?? isActive;
                    return <item.Icon active={active} />;
                  }}
                </NavLink>
              );
            })}
        </div>

        {/* What a page has put here, if anything. Empty it takes no room, so the
            pill is exactly the navigation until something arrives. */}
        <div
          ref={setSlot}
          data-testid="bottom-nav-slot"
          className={cn(
            'col-start-1 row-start-1 flex items-center gap-0.5 transition-all',
            !filled && 'pointer-events-none invisible scale-95 opacity-0',
          )}
          style={{ transitionDuration: `${BOTTOM_PILL_SWAP_MS}ms` }}
        />
      </div>
    </nav>
  );
}
